import { Search, Plus } from 'lucide-react'
import { useCatalog, useCurrency, useStoreContent, useProductLink, price, SORT_OPTIONS, isAvailable, productImage } from '../shared'
import StockBadge from '../../../../components/StockBadge'
import { t } from '../../../../i18n'

// Streetwear "drop": a dark stage with a scrolling marquee, huge uppercase
// type, a sticky filter sidebar and a dense numbered grid with a hover
// quick-add bar - loud, fast, built for browsing lots of stock.
export default function StoreBoldTemplate({ menu, addItem, storeName }) {
  const { products, filtered, categories, search, setSearch, category, setCategory, sort, setSort } = useCatalog(menu)
  const currency = useCurrency()
  const open = useProductLink()
  const content = useStoreContent({ storeName, title: 'New drop is live.', subtitle: 'Limited runs. When it is gone, it is gone.' })
  const ticker = `NEW DROP ✦ ${content.name.toUpperCase()} ✦ LIMITED STOCK ✦ ${products.length} PIECES ✦ `

  return (
    <div className="bold-drop bg-neutral-950 text-white rounded-3xl overflow-hidden" style={{ fontFamily: "'Arial Black', 'Helvetica Neue', Arial, sans-serif" }}>
      <div className="bg-lime-300 text-neutral-950 py-2 overflow-hidden whitespace-nowrap border-b-2 border-neutral-950" aria-hidden="true">
        <div className="marquee-track text-sm tracking-widest">{ticker.repeat(8)}</div>
      </div>

      <header className="px-6 md:px-10 pt-12 pb-10 border-b border-white/10">
        <p className="text-lime-300 text-xs tracking-[0.4em] mb-4">{content.name.toUpperCase()}</p>
        <h1 className="text-5xl md:text-8xl uppercase leading-[0.9] tracking-tight max-w-5xl">{content.heroTitle}</h1>
        <div className="flex flex-wrap items-end justify-between gap-6 mt-8" style={{ fontFamily: 'Inter, sans-serif' }}>
          <p className="text-white/60 max-w-md">{content.heroSubtitle}</p>
          <div className="flex gap-8 text-sm">
            <div><strong className="block text-3xl text-lime-300" style={{ fontFamily: 'inherit' }}>{products.length}</strong>{t("ITEMS")}</div>
            <div><strong className="block text-3xl text-lime-300">{categories.length}</strong>{t("CATEGORIES")}</div>
          </div>
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-[230px_minmax(0,1fr)]">
        <aside className="min-w-0 border-b lg:border-b-0 lg:border-e border-white/10 p-6 lg:sticky lg:top-20 lg:self-start" style={{ fontFamily: 'Inter, sans-serif' }}>
          <label className="flex items-center gap-2 bg-white/10 rounded-lg px-3 py-2.5 mb-6">
            <Search size={16} className="text-white/50" />
            <input aria-label={t("Search products")} value={search} onChange={e => setSearch(e.target.value)} placeholder={t("SEARCH")} className="bg-transparent outline-none text-sm w-full placeholder:text-white/40" />
          </label>
          <p className="text-[10px] text-white/40 tracking-[0.3em] mb-3">{t("SHOP")}</p>
          <ul className="flex lg:flex-col gap-1 overflow-x-auto no-scrollbar pb-2 lg:pb-0">
            {[{ id: 'all', name: t('Everything'), items: products }, ...categories].map(c => (
              <li key={c.id}>
                <button onClick={() => setCategory(String(c.id))} aria-pressed={category === String(c.id)}
                  className={`w-full flex justify-between gap-4 px-3 py-2 rounded-md text-sm font-bold uppercase whitespace-nowrap ${category === String(c.id) ? 'bg-lime-300 text-neutral-950' : 'text-white/70 hover:bg-white/10'}`}>
                  <span>{c.name}</span><span className="opacity-60">{c.items?.length || 0}</span>
                </button>
              </li>
            ))}
          </ul>
          <p className="text-[10px] text-white/40 tracking-[0.3em] mt-6 mb-3 hidden lg:block">{t("SORT")}</p>
          <div className="hidden lg:flex flex-col gap-1">
            {SORT_OPTIONS.map(([v, l]) => (
              <button key={v} onClick={() => setSort(v)} className={`text-start px-3 py-1.5 text-sm rounded-md ${sort === v ? 'text-lime-300' : 'text-white/60 hover:text-white'}`}>{sort === v ? '→ ' : ''}{t(l)}</button>
            ))}
          </div>
        </aside>

        <section className="min-w-0 p-4 md:p-6">
          {filtered.length === 0 ? (
            <p className="text-center text-white/50 py-20" style={{ fontFamily: 'Inter, sans-serif' }}>{t("NOTHING MATCHES. TRY ANOTHER SEARCH.")}</p>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {filtered.map((item, i) => (
                <article key={item.id} className="group relative bg-neutral-900 rounded-xl overflow-hidden">
                  <button onClick={() => open(item)} className="block w-full aspect-square overflow-hidden" aria-label={t("View {name}", { name: item.name })}>
                    {productImage(item)
                      ? <img loading="lazy" src={productImage(item)} alt={item.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                      : <span className="grid place-items-center h-full text-5xl text-white/10">#{String(i + 1).padStart(2, '0')}</span>}
                  </button>
                  <span className="absolute top-2 start-2 bg-neutral-950/80 text-white text-[11px] px-2 py-0.5 rounded">#{String(i + 1).padStart(2, '0')}</span>
                  <span className="absolute top-2 end-2 bg-lime-300 text-neutral-950 text-xs font-black px-2 py-0.5 rounded" style={{ fontFamily: 'Inter, sans-serif' }}>{price(item.price, currency)}</span>
                  <div className="p-3">
                    <button onClick={() => open(item)} className="text-sm uppercase tracking-wide text-start leading-tight">{item.name}</button>
                    <p className="text-[11px] text-white/40 mt-1" style={{ fontFamily: 'Inter, sans-serif' }}>{item.categoryName}</p>
                    {isAvailable(item) && item.stock_left && <StockBadge item={item} className="mt-2" />}
                  </div>
                  {isAvailable(item) ? (
                    <button onClick={() => addItem(item)} className="w-full md:absolute md:bottom-0 md:inset-x-0 md:translate-y-full md:group-hover:translate-y-0 md:focus:translate-y-0 transition-transform bg-lime-300 text-neutral-950 py-3 text-sm font-black flex items-center justify-center gap-2">
                      <Plus size={16} /> {t("QUICK ADD")}
                    </button>
                  ) : (
                    <div className="px-3 pb-3"><StockBadge item={item} /></div>
                  )}
                </article>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  )
}
